import { forwardRef, MouseEvent, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { ButtonProps } from './button.types';
import {
  ContentWrapper,
  IconWrapper,
  Label,
  PendingWrapper,
  StyledButton,
  StyledCounter,
} from './button.styled';
import { InlineSpinner } from '../../loaders/inline-spinner/inline-spinner';
import { Text } from '../text';

import { useButtonGroup } from './button-group';

/**
 * Универсальная кнопка дизайн-системы.
 *
 * Подробная документация и примеры: [README.md](./README.md)
 */
export const Button = observer(
  forwardRef<HTMLButtonElement, ButtonProps>((props, forwardedRef) => {
    const buttonGroupContext = useButtonGroup();

    const {
      mode = buttonGroupContext?.mode ?? 'accent',
      variant = buttonGroupContext?.variant ?? 'main',
      size = buttonGroupContext?.size ?? 'm',
      iconButton,
      leftIcon,
      rightIcon,
      pending: pendingProp,
      disabled: disabledProp,
      selected,
      counter,
      timer,
      children,
      type = 'button',
      ...restProps
    } = props;

    const pending = pendingProp ?? buttonGroupContext?.pending;
    const disabled = disabledProp ?? buttonGroupContext?.disabled;

    const isIconOnly = !!iconButton;

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (!pending) {
        restProps.onClick?.(e);
      }
    };

    const content = useMemo(() => {
      if (isIconOnly) {
        return <IconWrapper aria-hidden="true">{iconButton}</IconWrapper>;
      }

      return (
        <>
          {leftIcon && <IconWrapper aria-hidden="true">{leftIcon}</IconWrapper>}

          {children != null &&
            typeof children !== 'boolean' &&
            children !== '' && <Label>{children}</Label>}

          {timer && (
            <Text variant="numbers-13" aria-label={`таймер: ${timer}`}>
              {timer}
            </Text>
          )}

          {!!counter && counter > 0 && (
            <StyledCounter count={counter} size="s" />
          )}

          {rightIcon && (
            <IconWrapper aria-hidden="true">{rightIcon}</IconWrapper>
          )}
        </>
      );
    }, [children, counter, iconButton, isIconOnly, leftIcon, rightIcon, timer]);

    return (
      <StyledButton
        ref={forwardedRef}
        type={type}
        {...restProps}
        $isIconButton={isIconOnly}
        $mode={mode}
        $variant={variant}
        $size={size}
        // ARIA атрибуты
        aria-label={
          restProps['aria-label'] ??
          (typeof children === 'string' ? children : undefined)
        }
        aria-disabled={disabled || pending}
        aria-busy={pending}
        aria-pressed={selected}
        aria-describedby={
          [timer ? 'button-timer' : null, counter ? 'button-counter' : null]
            .filter(Boolean)
            .join() || undefined
        }
        // Data атрибуты для стилизации
        data-pending={pending}
        data-selected={selected}
        disabled={disabled}
        onClick={handleClick}
      >
        {pending && (
          <PendingWrapper>
            <InlineSpinner />
            {/* Скрытый текст для скринридеров */}
            <span style={{ display: 'none' }}>Загрузка...</span>
          </PendingWrapper>
        )}

        <ContentWrapper $pending={pending}>{content}</ContentWrapper>
      </StyledButton>
    );
  })
);
